import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import ChipMultiSelect from '../../../sharedModules/form/components/ChipMultiSelect';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import ClearIcon from '@mui/icons-material/Clear';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

import { format, subDays } from 'date-fns';
import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';
import DatePickerField from '../../../sharedModules/form/components/DatePickerField';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import VendorInvoiceNotesModal from '../components/VendorInvoiceNotesModal';

const STATUS_OPTIONS = [
  { value: 'unvalidated', label: 'Unvalidated' },
  { value: 'matched', label: 'Matched' },
  { value: 'discrepancy', label: 'Discrepancy' },
  { value: 'unmatched', label: 'Unmatched' },
  { value: 'ignored', label: 'Ignored' },
  { value: 'parsing_statement', label: 'Parsing Statement' },
];

const DEFAULT_STATUSES = ['unvalidated', 'discrepancy', 'unmatched', 'parsing_statement'];

const getStatusChip = (status: string) => {
  const statusConfig: Record<string, { label: string; color: any; icon: any }> = {
    matched: { label: 'Matched', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    discrepancy: { label: 'Discrepancy', color: 'warning', icon: <WarningIcon fontSize="small" /> },
    unmatched: { label: 'Unmatched', color: 'error', icon: <CancelIcon fontSize="small" /> },
    ignored: { label: 'Ignored', color: 'default', icon: <BlockIcon fontSize="small" /> },
    parsing_statement: { label: 'Parsing Statement', color: 'info', icon: <HourglassEmptyIcon fontSize="small" /> },
    unvalidated: { label: 'Unvalidated', color: 'default', icon: <HelpOutlineIcon fontSize="small" /> },
    extraction_failed: { label: 'Extraction Failed', color: 'error', icon: <ErrorIcon fontSize="small" /> },
  };

  const config = statusConfig[status] || statusConfig.unvalidated;
  return <Chip label={config.label} color={config.color} size="small" icon={config.icon} />;
};

const getColumns = (onNotesClick: (invoiceId: number, vendorName: string) => void): IDataTableColumn<any>[] => [
  {
    name: 'Vendor Name',
    selector: 'resolved_vendor_name',
    sortable: true,
    wrap: true,
    format: (row: any) => (
      <Link
        to={`/vendors/invoices/${row.id}`}
        style={{ color: 'inherit', textDecoration: 'none' }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {row.resolved_vendor_name || '-'}
      </Link>
    ),
  },
  {
    name: 'Vendor ID',
    selector: 'vendor_id',
    sortable: true,
    format: (row: any) => row.vendor_id || '-',
  },
  {
    name: 'Received At',
    selector: 'email_received_at',
    sortable: true,
    format: (row: any) => {
      const date = row.email_received_at || row.created_at;
      if (!date) return '-';
      return new Date(date).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  {
    name: 'Total Amount',
    selector: 'line_items_total',
    sortable: true,
    format: (row: any) => {
      const total = parseFloat(row.line_items_total);
      if (!total && total !== 0) return '-';
      return `$${total.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  {
    name: 'Validation Status',
    selector: 'validation_status',
    sortable: true,
    format: (row: any) => {
      if (row.extraction_error) return getStatusChip('extraction_failed');
      return getStatusChip(row.display_status || row.validation_status || 'unvalidated');
    },
  },
  {
    name: 'ServMan',
    selector: 'servman_matched_count',
    sortable: false,
    format: (row: any) => {
      const matched = row.servman_matched_count || 0;
      const active = (row.line_items_count || 0) - (row.ignored_count || 0);
      if (active === 0) return '-';
      const color = matched === active ? 'success' : matched > 0 ? 'warning' : 'error';
      return <Chip label={`${matched}/${active}`} color={color} size="small" variant="outlined" />;
    },
  },
  {
    name: 'DocuWare',
    selector: 'docuware_matched_count',
    sortable: false,
    format: (row: any) => {
      const matched = row.docuware_matched_count || 0;
      const active = (row.line_items_count || 0) - (row.ignored_count || 0);
      if (active === 0) return '-';
      const color = matched === active ? 'success' : matched > 0 ? 'warning' : 'error';
      return <Chip label={`${matched}/${active}`} color={color} size="small" variant="outlined" />;
    },
  },
  {
    name: 'Unmatched',
    selector: 'unmatched_count',
    sortable: false,
    format: (row: any) => {
      const unmatched = row.unmatched_count || 0;
      const active = (row.line_items_count || 0) - (row.ignored_count || 0);
      if (active === 0) return '-';
      if (unmatched === 0) return <Chip label="0" color="success" size="small" variant="outlined" />;
      return <Chip label={String(unmatched)} color="error" size="small" icon={<CancelIcon />} />;
    },
  },
  {
    name: 'Last Note',
    selector: 'last_comment_at',
    sortable: true,
    format: (row: any) => {
      if (!row.last_comment_at) return '-';
      return new Date(row.last_comment_at).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    },
  },
  {
    name: 'Notes',
    selector: 'comments_count',
    sortable: false,
    format: (row: any) => {
      const count = row.comments_count || 0;
      const flagged = Boolean(row.flagged_for_review);
      return (
        <IconButton
          size="small"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onNotesClick(row.id, row.resolved_vendor_name);
          }}
          title={flagged ? 'Flagged for review' : 'View notes'}
        >
          <Badge
            badgeContent={count}
            color={flagged ? 'error' : 'primary'}
            max={99}
            showZero
            sx={{
              '& .MuiBadge-badge': {
                minWidth: 22,
                height: 20,
                padding: '0 6px',
                fontSize: '0.75rem',
                lineHeight: '20px',
                whiteSpace: 'nowrap',
              },
            }}
          >
            <ChatBubbleOutlineIcon fontSize="small" color={flagged ? 'error' : 'inherit'} />
          </Badge>
        </IconButton>
      );
    },
  },
];

const VendorInvoiceListPage: React.FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const { showErrorSnackBar } = useContext(SnackBarContext);

  const queryParams = new URLSearchParams(location.search);
  const initialSearch = queryParams.get('search') || '';
  const initialStatuses = queryParams.get('status') ? queryParams.get('status')!.split(',') : DEFAULT_STATUSES;
  const defaultAfter = format(subDays(new Date(), 30), 'yyyy-MM-dd');
  const defaultBefore = format(new Date(), 'yyyy-MM-dd');
  const initialReceivedAfter = queryParams.get('received_after') || defaultAfter;
  const initialReceivedBefore = queryParams.get('received_before') || defaultBefore;
  const initialLocation = queryParams.get('location') || 'all';

  const [invoices, setInvoices] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>(initialSearch);
  const [statusFilter, setStatusFilter] = useState<string[]>(initialStatuses);
  const [receivedAfter, setReceivedAfter] = useState<string>(initialReceivedAfter);
  const [receivedBefore, setReceivedBefore] = useState<string>(initialReceivedBefore);
  const [locationFilter, setLocationFilter] = useState<string>(initialLocation);
  const [loading, setLoading] = useState<boolean>(false);
  const [notesModal, setNotesModal] = useState<{ open: boolean; invoiceId: number; vendorName: string }>({
    open: false,
    invoiceId: 0,
    vendorName: '',
  });
  const fetchControllerRef = useRef<AbortController | null>(null);

  const updateUrl = (search: string, statuses: string[], after: string, before: string, loc: string) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    const isDefault =
      statuses.length === DEFAULT_STATUSES.length && statuses.every((s) => DEFAULT_STATUSES.includes(s));
    if (!isDefault) params.set('status', statuses.join(','));
    if (after && after !== defaultAfter) params.set('received_after', after);
    if (before && before !== defaultBefore) params.set('received_before', before);
    if (loc && loc !== 'all') params.set('location', loc);
    const qs = params.toString();
    history.replace(`/vendors/invoices${qs ? `?${qs}` : ''}`);
  };

  const fetchInvoices = async (search = '', statuses: string[] = [], after = '', before = '', loc = 'all') => {
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (statuses.length > 0) params.validation_status = statuses.join(',');
      if (after) params.received_after = after;
      if (before) params.received_before = before;
      if (loc && loc !== 'all') params.location = loc;

      const response: any = await axios.get('/vendors/vendor_invoices', { params, signal: controller.signal });
      setInvoices(response.data || []);
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch vendor statements';
      showErrorSnackBar(errorMessage);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handleSearch = () => {
    updateUrl(searchTerm, statusFilter, receivedAfter, receivedBefore, locationFilter);
    fetchInvoices(searchTerm, statusFilter, receivedAfter, receivedBefore, locationFilter);
  };

  const handleStatusChange = (newStatuses: string[]) => {
    setStatusFilter(newStatuses);
    updateUrl(searchTerm, newStatuses, receivedAfter, receivedBefore, locationFilter);
    fetchInvoices(searchTerm, newStatuses, receivedAfter, receivedBefore, locationFilter);
  };

  const handleClearStatuses = () => {
    setStatusFilter([]);
    updateUrl(searchTerm, [], receivedAfter, receivedBefore, locationFilter);
    fetchInvoices(searchTerm, [], receivedAfter, receivedBefore, locationFilter);
  };

  const handleDateChange = (field: 'after' | 'before', value: string) => {
    const newAfter = field === 'after' ? value : receivedAfter;
    const newBefore = field === 'before' ? value : receivedBefore;
    if (field === 'after') setReceivedAfter(value);
    else setReceivedBefore(value);
    updateUrl(searchTerm, statusFilter, newAfter, newBefore, locationFilter);
    fetchInvoices(searchTerm, statusFilter, newAfter, newBefore, locationFilter);
  };

  const handleLocationChange = (newLocation: string) => {
    setLocationFilter(newLocation);
    updateUrl(searchTerm, statusFilter, receivedAfter, receivedBefore, newLocation);
    fetchInvoices(searchTerm, statusFilter, receivedAfter, receivedBefore, newLocation);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleNotesClick = (invoiceId: number, vendorName: string) => {
    setNotesModal({ open: true, invoiceId, vendorName });
  };

  const columns = getColumns(handleNotesClick);

  useEffect(() => {
    fetchInvoices(initialSearch, initialStatuses, initialReceivedAfter, initialReceivedBefore, initialLocation);
  }, []);

  return (
    <Box style={{ width: '100%' }}>
      <Box>
        <Grid justifyContent="space-between" container spacing={3}>
          <Grid item>
            <Typography variant="h5" component="h5" style={{ marginTop: 5 }}>
              Vendor Statements
            </Typography>
          </Grid>
          <Grid item>
            <Button
              onClick={() => history.push('/vendors/invoices/upload')}
              variant="contained"
              startIcon={<UploadFileIcon />}
              style={{ marginTop: 5 }}
            >
              Upload Statement
            </Button>
          </Grid>
        </Grid>
        <br />
        <Grid container spacing={2} alignItems="flex-start" onKeyDown={handleKeyPress} style={{ marginBottom: 16 }}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Search by Vendor or Invoice Number"
              variant="standard"
              style={{ width: '100%', marginTop: 8 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter vendor name or invoice number"
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <DatePickerField
              label="Received After"
              value={receivedAfter}
              onChange={(val) => handleDateChange('after', val)}
              fullWidth
              style={{ marginTop: 8 }}
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <DatePickerField
              label="Received Before"
              value={receivedBefore}
              onChange={(val) => handleDateChange('before', val)}
              fullWidth
              style={{ marginTop: 8 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
              <ChipMultiSelect
                label="Validation Status"
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={handleStatusChange}
                showCheckboxes
                style={{ width: '100%', marginTop: 8 }}
              />
              {statusFilter.length > 0 && (
                <IconButton size="small" onClick={handleClearStatuses} title="Clear filters">
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ marginTop: 2 }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginBottom: 0.5 }}>
                Location
              </Typography>
              <ToggleButtonGroup
                value={locationFilter}
                exclusive
                onChange={(_e, val) => val && handleLocationChange(val)}
                size="small"
                color="primary"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="pittsburgh">Pittsburgh</ToggleButton>
                <ToggleButton value="tampa">Tampa</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button onClick={handleSearch} variant="contained" style={{ marginTop: 20 }}>
              Search
            </Button>
          </Grid>
        </Grid>
        <Card>
          <CardContent>
            <DataTable
              title="Invoices"
              columns={columns}
              data={invoices}
              onRowClicked={(row) => history.push(`/vendors/invoices/${row.id}`)}
              striped
              dense
              highlightOnHover
              pointerOnHover
              noHeader
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100]}
              progressPending={loading}
            />
          </CardContent>
        </Card>
      </Box>

      <VendorInvoiceNotesModal
        open={notesModal.open}
        onClose={() => setNotesModal({ open: false, invoiceId: 0, vendorName: '' })}
        invoiceId={notesModal.invoiceId}
        vendorName={notesModal.vendorName}
        onNotesChanged={() => fetchInvoices(searchTerm, statusFilter, receivedAfter, receivedBefore, locationFilter)}
      />
    </Box>
  );
};

export default VendorInvoiceListPage;
